<i18n src="./resources/locales.json"></i18n>

<template>
  <v-snackbar v-model="show" :style="CSS_VARS" :timeout="timeout" color="surface" bottom left>
    <v-list-item>
      <v-list-item-icon>
        <v-icon class="message-icon">{{ icon }}</v-icon>
      </v-list-item-icon>
      <v-list-item-content :class="itemContentClass">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span v-html="message"></span>
      </v-list-item-content>
      <v-list-item-action>
        <v-btn v-if="showClose" color="primary" text @click="cleanSnackbar">
          <span v-once>{{ $t('messages.close') }}</span>
        </v-btn>
      </v-list-item-action>
    </v-list-item>
  </v-snackbar>
</template>

<script>
export default {
  name: 'EcsSnackbar',

  data() {
    return {
      show: false,
      message: '',
      icon: '',
      iconColor: '',
      timeout: 3000,
      showClose: false,
      messageIcons: {
        info: 'mdi-information-outline',
        success: 'mdi-check-circle-outline',
        error: 'mdi-close-octagon-outline',
        warning: 'mdi-alert-outline'
      },
      messageIconsColors: {
        info: 'var(--v-info-base)',
        success: 'var(--v-success-base)',
        error: 'var(--v-error-base)',
        warning: 'var(--v-warning-base)'
      },
      setTimeoutId: null
    };
  },

  sockets: {
    SERVER_NOTIFICATION_EVENT(event) {
      const { isError, notification } = event;
      let finalNotification = notification;

      // Re-write the notification message if it contains 'not enough licences' string
      if (notification && notification.toLowerCase().includes('not enough licences')) {
        finalNotification = this.$t('overwrite.notEnoughLicences');
      }

      const snackbar = {
        type: isError ? 'error' : 'info',
        message: finalNotification
      };
      this.setupSnackbar(snackbar);
    }
  },

  computed: {
    CSS_VARS() {
      return {
        '--icon-color': this.iconColor
      };
    },

    itemContentClass() {
      return `message-text-${this.$vuetify.theme.dark ? 'dark' : 'light'}`;
    }
  },

  mounted() {
    this.$root.$on('snackbar-message', this.setupSnackbar);
  },

  beforeDestroy() {
    this.$root.$off('snackbar-message', this.setupSnackbar);
  },

  methods: {
    /**
     * Setup and display the snackbar based on the config object.
     * If the snackbar type config (info/success/error/warning) is correct and defined then it will display a proper icon.
     * If the snackbar type config is 'error' then snackbar will be sticky (timeout = 0)
     * If the snackbar timeout config is defined then it will take that config otherwise timeout will default (3s)
     * @param {Object} snackbar - The snackbar configuration object { message, type, timeout }
     */
    setupSnackbar(snackbar) {
      this.cleanSnackbar();
      // Setting message
      this.message = snackbar.message;
      // Setting icon
      this.icon = this.messageIcons[snackbar.type] || this.icon;
      this.iconColor = this.messageIconsColors[snackbar.type] || this.iconColor;
      // Setting timeout
      if (snackbar.type === 'error') {
        this.timeout = 0;
      } else if (!isNaN(snackbar.timeout)) {
        this.timeout = snackbar.timeout;
      }
      // Ensure to clean up snackbar after a timeout time.
      if (this.timeout > 0) {
        this.setTimeoutId = setTimeout(() => {
          this.cleanSnackbar();
        }, this.timeout);
      }
      // Display close button if snackbar is sticky (timeout = 0).
      this.showClose = snackbar.showClose || this.timeout === 0;
      // Display snackbar
      this.show = true;
    },
    /**
     * This method will clean the snackbar to default values.
     */
    cleanSnackbar() {
      if (this.setTimeoutId) {
        clearTimeout(this.setTimeoutId);
      }
      this.message = '';
      this.icon = '';
      this.iconColor = '';
      this.timeout = 3000;
      this.showClose = false;
      this.show = false;
      this.setTimeoutId = null;
    }
  }
};
</script>

<style scoped>
.message-icon {
  color: var(--icon-color);
}
.message-text-dark {
  color: #fff;
}
.message-text-light {
  color: #666;
}
</style>
